import Layout from 'components/layout'
import { GetServerSideProps } from 'next'
import strapi from 'services/strapi'
import BlogPost from 'models/blog-post'
import DateView from 'components/date-view'
import Avatar from 'components/avatar'
import Link from 'next/link'
import routes from 'routes'
import { NextSeo } from 'next-seo'
import { CANONICAL_PREFIX } from 'config'
import Breadcrumb from 'components/breadcrumb'
import { Heading } from 'pages'
import AppDownload from 'components/app-download'
import TagList from 'components/tag-list'
import api from 'services/api'
import Tag from 'models/tag'

const BlogPostListPage = ({
  posts,
  tags,
}: {
  posts: BlogPost[]
  tags: Tag[]
}): JSX.Element => {
  return (
    <Layout showDownloadBtn={false}>
      <NextSeo title="Blog" canonical={`${CANONICAL_PREFIX}/blog`} />
      <section className="desktop:flex">
        <section className="desktop:w-[1018px] mb-4">
          <div className="hidden lg:block px-4 py-3">
            <Breadcrumb
              className="mr-4"
              items={[
                {
                  name: 'Blog',
                },
              ]}
            />
          </div>
          <div className="p-4">
            {posts.map((post, index) => {
              return (
                <div key={post.slug + index}>
                  <BlogPostItem post={post} />
                  <hr className="border-gray-300 my-8" />
                </div>
              )
            })}
          </div>
        </section>
        <section className="desktop:w-[422px]">
          <section className="px-4 mb-[40px]">
            <Heading title="Tags" moreLink={routes.tag.list()} />
            <TagList tags={tags} />
          </section>
          <section className="px-4 mb-[40px]">
            <AppDownload />
          </section>
        </section>
      </section>
    </Layout>
  )
}

const BlogPostItem = ({ post }: { post: BlogPost }): JSX.Element => {
  const titleImageUrl =
    post.title_image?.formats?.large?.url ??
    post.title_image?.formats?.medium?.url ??
    post.title_image?.formats?.small?.url
  return (
    <article>
      <Link href={routes.blog.post({ slug: post.slug })}>
        <h2 className="text-2xl font-bold font-body">{post.title}</h2>
        <div className="flex my-1">
          <Avatar
            src={post.editor.avatar.formats.thumbnail.url}
            name={post.editor.name}
            className="w-5 h-5"
          />
          <span className="pl-1 font-medium">{post.editor.name}</span>
          <span className="text-gray-400 px-2"> | </span>
          <DateView date={post.updatedAt} className="text-gray-400" />
        </div>
        {titleImageUrl && (
          <img
            src={titleImageUrl}
            className="my-4 rounded-lg w-full"
            alt={post.title}
          />
        )}
      </Link>
    </article>
  )
}

export const getServerSideProps: GetServerSideProps = async () => {
  const posts = await strapi.blogPost.getList({
    start: 0,
    limit: 99,
  })
  const tags = (await api.tag.getHomeTags()) || []
  return {
    props: {
      posts,
      tags,
    },
  }
}

export default BlogPostListPage
